// pages/Yourdoubts.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  AccountCircle as AccountCircleIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Event as EventIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../../api";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";

const peerCampusGradient =
  "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)";

export default function YourDoubts({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const gradientTextSx = {
    background: peerCampusGradient,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    display: 'inline-block',
  };

  const gradientOutlinedButtonSx = {
    border: '1px solid transparent',
    background: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, ${peerCampusGradient} border-box`,
    '&:hover': {
      background: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, ${peerCampusGradient} border-box`,
    },
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: '#016f57',
    },
  };

  const [doubts, setDoubts] = useState([]);
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [newQuestion, setNewQuestion] = useState("");
  const [commentText, setCommentText] = useState("");
  const [editDoubt, setEditDoubt] = useState(null);
  const [editText, setEditText] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const commentsEndRef = useRef(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Define sidebar items to pass to Navbar
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <AccountCircleIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/doubts', 
      action: () => navigate('/doubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Your Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />,
      active: true
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <EventIcon />
    },
  ];

  // Fetch doubts from API
  const fetchDoubts = async () => {
    try { 
      const response = await api.get('doubts/doubt/');
      // console.log("API Response:", response.data);
      
      // Handle different API response structures
      if (response.data && Array.isArray(response.data)) {
        setDoubts(response.data);
      } else if (response.data && response.data.results) {
        setDoubts(response.data.results);
      } else if (response.data && response.data.data) {
        setDoubts(response.data.data);
      } else {
        console.error("Unexpected API response structure:", response.data);
        setDoubts([]);
      }
    } catch (error) {
      console.error('Error fetching doubts:', error);
      // Fallback to empty array if API fails
      setDoubts([]);
      setSnackbarMessage("Failed to load doubts");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchDoubts();
  }, []);

  // Add new doubt
  const handleAddDoubt = async () => {
    if (!newQuestion.trim()) {
      setSnackbarMessage("Please enter a question");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const response = await api.post("doubts/createview/", {
        question: newQuestion,
      });

      if (response.data) {
        setDoubts(prev => [response.data, ...prev]); // Add instantly
        setNewQuestion("");
        setSnackbarMessage("Doubt created successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error creating doubt:", error);
      setSnackbarMessage("Error creating doubt");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Edit doubt
  const handleEditDoubt = async () => {
    if (!editText.trim() || !editDoubt) return;
    
    try {
      const response = await api.put(`doubts/update-doubt/${editDoubt.id}/`, {
        question: editText,
      });
      
      if (response.data) {
        setDoubts(prev => 
          prev.map(d => d.id === editDoubt.id ? response.data : d)
        );
        setEditDoubt(null);
        setEditText("");
        setSnackbarMessage("Doubt updated successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating doubt:", error);
      setSnackbarMessage("Error updating doubt");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Delete doubt
  const handleDeleteDoubt = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`doubts/update-doubt/${deleteConfirm.id}/`);
      setDoubts(prev => prev.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      setSnackbarMessage("Doubt deleted successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting doubt:", error);
      setSnackbarMessage("Error deleting doubt");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Add comment
  const handleAddComment = () => {
    if (!commentText.trim() || !selectedDoubt) {
      setSnackbarMessage("Please enter a comment");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    
    const updatedDoubt = {
      ...selectedDoubt,
      comments: [...(selectedDoubt.comments || []), 
                { user: "You", text: commentText, createdAt: new Date().toISOString() }],
    };
    
    setSelectedDoubt(updatedDoubt);
    setDoubts(prevDoubts => 
      prevDoubts.map(d => d.id === selectedDoubt.id ? updatedDoubt : d)
    );
    setCommentText("");
    setSnackbarMessage("Comment added successfully");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (selectedDoubt) {
      commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedDoubt]);

  // Open edit dialog
  const openEditDialog = (doubt) => {
    setEditDoubt(doubt);
    setEditText(doubt.question);
  };

  // Open delete confirmation
  const openDeleteDialog = (doubt) => {
    setDeleteConfirm(doubt);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };
  
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: embedded ? "auto" : "100vh" }}>
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Only show sidebar on desktop */}
        {!embedded && !isMobile && <GlobalSidebar />}
        
        <Box component="main" sx={{ 
          flexGrow: 1, 
          overflowX: "hidden",
          bgcolor: "#f5f7fa"
        }}>
          <Box sx={{ p: embedded ? { xs: 2, sm: 3, md: 4 } : { xs: 3, sm: 4, md: 5 } }}>
            {!embedded && <BackButton sx={{ mb: 2 , backgroundColor: "#dfdfdff", color: "#000" }} />}
            {!selectedDoubt ? (
              <>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ fontSize: { xs: "1.8rem", md: "2.5rem" } }}
                >
                  <Box component="span" sx={gradientTextSx}>
                  Your Doubts
                  </Box>
                </Typography>

                {/* Add New Question */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    display: "flex",
                    gap: 2,
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(6px)",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Ask a new doubt..."
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddDoubt()}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      minWidth: { xs: "100%", sm: "auto" },
                      textTransform: 'none',
                      background: peerCampusGradient,
                      '&:hover': { background: peerCampusGradient },
                    }}
                    onClick={handleAddDoubt}
                  >
                    Post
                  </Button>
                </Paper>

                {/* Doubts List */}
                <Box sx={{ display: "grid", gap: 2 }}>
                  {doubts.length > 0 ? (
                    doubts.map((doubt) => (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        key={doubt.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <Paper
                          elevation={4}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            bgcolor: "rgba(255,255,255,0.85)",
                            backdropFilter: "blur(6px)",
                            position: "relative",
                          }}
                        >
                          {/* Action buttons */}
                          <Box sx={{ position: "absolute", top: 70, right: 8, display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDialog(doubt);
                              }}
                              sx={{ color: "primary.main" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteDialog(doubt);
                              }}
                              sx={{ color: "error.main" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Box 
                            sx={{ display: "flex", justifyContent: "space-between", flexWrap: 'wrap', gap: 1 }}
                            onClick={() => setSelectedDoubt(doubt)}
                          >
                            <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, minWidth: 200 }}>
                              {doubt.question}
                            </Typography>
                            <Chip
                              label={doubt.status || "Open"}
                              color={(doubt.status || "Open") === "Open" ? "success" : "warning"}
                              size="small"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Posted by {doubt.user?.username || "Unknown"} • {formatDate(doubt.created_at)}
                          </Typography>
                        </Paper>
                      </motion.div>
                    ))
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                      No doubts found. Be the first to ask a question!
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <>
                {/* Back Button */}
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setSelectedDoubt(null)}
                  sx={{ mb: 3, borderRadius: 2, textTransform: 'none', ...gradientOutlinedButtonSx }}
                >
                  <Box component="span" sx={gradientTextSx}>Back to Doubts</Box>
                </Button>

                {/* Selected Doubt */}
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    mb: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: 'wrap', gap: 1, alignItems: "flex-start" }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, flex: 1, mr: 2 }}>
                      {selectedDoubt.question}
                    </Typography>
                    <Chip
                      label={selectedDoubt.status || "Open"}
                      color={(selectedDoubt.status || "Open") === "Open" ? "success" : "warning"}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    By {selectedDoubt.user?.username || "Unknown"} • {formatDate(selectedDoubt.created_at)}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* AI Answer */}
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "#e3f2fd",
                      borderRadius: 2,
                      position: "relative",
                    }}
                    elevation={1}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      AI Suggested Answer
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {selectedDoubt.ai_suggestions || "No AI suggestions available yet."}
                    </Typography>
                    {selectedDoubt.ai_suggestions && (
                      <IconButton
                        size="small"
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedDoubt.ai_suggestions);
                          setSnackbarMessage("Answer copied to clipboard");
                          setSnackbarSeverity("success");
                          setSnackbarOpen(true);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>
                </Paper>

                {/* Comments Section */}
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Comments
                  </Typography>
                  
                  {(selectedDoubt.comments && selectedDoubt.comments.length > 0) ? (
                    selectedDoubt.comments.map((comment, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          mb: 1,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: "rgba(250,250,250,0.9)",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        <Avatar sx={{ bgcolor: "#1976d2" }}>{comment.user[0]}</Avatar>
                        <Box>
                          <Typography variant="subtitle2">{comment.user}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {comment.text}
                          </Typography>
                          {comment.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(comment.createdAt)}
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No comments yet. Be the first to comment!
                    </Typography>
                  )}
                  
                  <div ref={commentsEndRef} />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Add Comment */}
                  <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                      sx={{ borderRadius: 3 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddComment}
                      sx={{
                        borderRadius: 3,
                        minWidth: { xs: "100%", sm: "auto" },
                        textTransform: 'none',
                        background: peerCampusGradient,
                        '&:hover': { background: peerCampusGradient },
                      }}
                    >
                      Post
                    </Button>
                  </Box>
                </Paper>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Doubt Dialog */}
      <Dialog
        open={Boolean(editDoubt)}
        onClose={() => setEditDoubt(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Doubt</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question"
            fullWidth
            variant="outlined"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDoubt(null)}>Cancel</Button>
          <Button 
            onClick={handleEditDoubt} 
            variant="contained"
            disabled={!editText.trim()}
            sx={{
              textTransform: 'none',
              background: peerCampusGradient,
              '&:hover': { background: peerCampusGradient },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Doubt</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the doubt "{deleteConfirm?.question}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDeleteDoubt} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {!embedded && (
        <div ref={footerRef}>
          <PostLoginFooter />
        </div>
      )}
    </Box>
  );
}